import "./NewSectionArea.less";

import { notification, Button, Card, Empty, Spin } from "antd";
import { cloneDeep } from "lodash";
import * as React from "react";

import { OrderedEntryGroupApi, OrderedListingGroupApi, OrderedSectionApi } from "lib/api";
import { CancellationToken } from "lib/api/base";
import { IBaseProps } from "lib/data/interfaces";
import {
	ComponentTemplate,
	EntryGroup,
	EntryGroupTemplate,
	ListingGroup,
	ListingGroupTemplate,
	OrderedDataFieldTemplate,
	OrderedEntryGroup,
	OrderedListingGroup,
	OrderedSection,
	RequirementItem,
	Section,
	SectionTemplate,
	SectionVersion
} from "lib/data/model";
import { IconType } from "lib/data/model/dictionaries/IconTypes";
import { Requiredness } from "lib/data/model/enumerations";
import { ComponentTemplateService, StateService } from "lib/services";

import { MiniTemplateCard } from "../TemplateCard";


interface INewSectionAreaProps extends IBaseProps {
	sectionVersion: SectionVersion;
	section: Section;
	sectionTemplate: SectionTemplate;
	onSave: () => void;
}

interface INewSectionAreaState {
	isLoading: boolean;
	isEditing: boolean;
	availableSections: SectionTemplate[] | undefined;
}


export class NewSectionArea extends React.Component<INewSectionAreaProps, INewSectionAreaState> {

	private apiCancellationToken: CancellationToken = new CancellationToken();


	private draft!: SectionVersion;
	private addableTemplates: Array<RequirementItem<ComponentTemplate> | undefined> = [];

	constructor(props: INewSectionAreaProps) {

		super(props);

		this.state = {
			isLoading: false,
			isEditing: false,
			availableSections: undefined
		};
	}


	public async componentWillMount() {

		// Store Draft
		this.draft = cloneDeep(this.props.sectionVersion);

		await this.processTemplates();
	}

	public async componentWillReceiveProps(newProps: INewSectionAreaProps) {

		// Store Draft
		this.draft = cloneDeep(newProps.sectionVersion);

		await this.processTemplates();
	}

	public componentWillUnmount() {

		// Cancel Outstanding API Calls
		this.apiCancellationToken.cancel();
	}

	public render() {

		// Check If Loading
		if (this.state.isLoading) {

			return (
				<Card
					className="addable-section-wrapper"
				>
					<Spin />
				</Card>
			);
		}
		else if (this.state.isEditing) {

			// Check If Any Templates Are Available
			if (this.addableTemplates.length > 0) {

				return (

					<Card
						className="addable-section-wrapper"
						extra={<Button icon="close" type="ghost" onClick={() => StateService.setCancelableState(() => this.setState({ isEditing: false }), this.apiCancellationToken)} />}
						title="Select Child Component Template"
					>
						<div className="new-child-component">

							<div className="new-child-component-template-selector">

								{(this.addableTemplates.filter((i) => i !== undefined) as (Array<RequirementItem<ComponentTemplate>>)).map((componentTemplate, key) => {
									return (
										<MiniTemplateCard
											className="new-child-component-template-card"
											onClick={() => this.addComponentToSection(componentTemplate)}
											key={key}
											title={componentTemplate.item.name}
											iconType={IconType.get(componentTemplate.item.$type)}
										/>
									);
								})}
							</div>
						</div>
					</Card>
				);
			}
			else {

				// Set Timer To Reset State
				setTimeout(() => StateService.setCancelableState(() => this.setState({ isEditing: false }), this.apiCancellationToken), 5000);

				return (

					<Card
						className="addable-section-wrapper"
						extra={<Button icon="close" type="ghost" onClick={() => StateService.setCancelableState(() => this.setState({ isEditing: false }), this.apiCancellationToken)} />}
						title="Select Child Component Template"
					>
						<Empty
							image={Empty.PRESENTED_IMAGE_SIMPLE}
							description="No Available Templates To Add"
						/>
					</Card>
				);
			}
		}
		else {

			return (

				<Card
					className="addable-section-wrapper"
				>
					<div className="new-child-component">
						<Button
							className="new-child-component-button"
							type="link"
							onClick={this.toggleIsEditing}
							block={true}
							icon="plus-circle"
						>
							Child Component
						</Button>
					</div>
				</Card>
			);
		}
	}

	private toggleIsEditing = () => {
		StateService.setCancelableState(() => this.setState({ isEditing: !this.state.isEditing }), this.apiCancellationToken);
	}

	private addComponentToSection = async (componentItem: RequirementItem<ComponentTemplate>) => {

		// Indicate Loading Is Occurring
		StateService.setCancelableState(() => this.setState({ isLoading: true }), this.apiCancellationToken);

		let successfulSave = false;

		switch (componentItem.item.$type) {

			case SectionTemplate.$type:

				// Construct Draft Version
				const newSectionVersion: SectionVersion = new SectionVersion(`New ${componentItem.item.name}`);

				// Construct Section
				const newSection: Section = new Section(componentItem.item as SectionTemplate, newSectionVersion);

				// Construct Ordered Section
				const newOrderedSection: OrderedSection = new OrderedSection(componentItem.initialOrder, newSection);

				// Save Section
				const savedSection = await OrderedSectionApi.create(this.props.section, newOrderedSection);

				// Check Section
				if (savedSection !== undefined) {

					// Store Ordered Section
					this.draft.sections.push(savedSection);

					successfulSave = true;
				}

				break;

			case EntryGroupTemplate.$type:

				// Construct Entry Group
				const newEntryGroup = new EntryGroup();
				newEntryGroup.name = componentItem.item.name;
				newEntryGroup.template = componentItem.item as EntryGroupTemplate;

				// Initialize Columns
				const entryGroupColumns: OrderedDataFieldTemplate[] = [];

				// Loop Through Requirements
				for (const requirement of (componentItem.item as EntryGroupTemplate).requirements.filter((i) => i.requiredness === Requiredness.REQUIRED)) {

					// Construct Ordered Data Field Template
					const orderedTemplate = new OrderedDataFieldTemplate();
					orderedTemplate.order = requirement.item.initialOrder;
					orderedTemplate.item = requirement.item.item;

					// Add Column
					entryGroupColumns.push(orderedTemplate);
				}

				// Set Columns
				newEntryGroup.columns = entryGroupColumns;

				// Construct Ordered Entry Group
				const newOrderedEntryGroup: OrderedEntryGroup = new OrderedEntryGroup();
				newOrderedEntryGroup.order = componentItem.initialOrder;
				newOrderedEntryGroup.item = newEntryGroup;

				// Save Entry Group
				const savedEntryGroup = await OrderedEntryGroupApi.save(this.props.section, newOrderedEntryGroup);

				// Check Entry Group
				if (savedEntryGroup !== undefined) {

					// Store Ordered Entry Group
					this.draft.entryGroups.push(savedEntryGroup);

					successfulSave = true;
				}

				break;

			case ListingGroupTemplate.$type:

				// Construct Listing Group
				const newListingGroup = new ListingGroup();
				newListingGroup.name = componentItem.item.name;
				newListingGroup.template = componentItem.item as ListingGroupTemplate;

				// Initialize Columns
				const listingGroupColumns: OrderedDataFieldTemplate[] = [];

				// Loop Through Requirements
				for (const requirement of (componentItem.item as ListingGroupTemplate).requirements.filter((i) => i.requiredness === Requiredness.REQUIRED)) {

					// Construct Ordered Data Field Template
					const orderedTemplate = new OrderedDataFieldTemplate();
					orderedTemplate.order = requirement.item.initialOrder;
					orderedTemplate.item = requirement.item.item;

					// Add Column
					listingGroupColumns.push(orderedTemplate);
				}

				// Set Columns
				newListingGroup.columns = listingGroupColumns;

				// Construct Ordered Listing Group
				const newOrderedListingGroup: OrderedListingGroup = new OrderedListingGroup();
				newOrderedListingGroup.order = componentItem.initialOrder;
				newOrderedListingGroup.item = newListingGroup;

				// Save Listing Group
				const savedListingGroup = await OrderedListingGroupApi.save(this.props.section, newOrderedListingGroup);

				// Check Listing Group
				if (savedListingGroup !== undefined) {

					// Store Ordered Listing Group
					this.draft.listingGroups.push(savedListingGroup);

					successfulSave = true;
				}

				break;

			default:

				break;
		}

		if (successfulSave === true) {

			notification.success({
				message: "Successfully added child component"
			});
		}
		else {

			notification.success({
				message: "Failed to add child component"
			});
		}

		// Disable Draft Edit Mode
		this.toggleIsEditing();

		// Refresh Section View
		this.props.onSave();

		// Indicate Loading Is NOT Occurring
		StateService.setCancelableState(() => this.setState({ isLoading: false }), this.apiCancellationToken);
	}

	private processTemplates = async () => {

		// Check For Need For Loading Indicator
		if (!this.state.isLoading && this.state.isEditing) {

			// Indicate Is Loading
			StateService.setCancelableState(() => this.setState({ isLoading: true }), this.apiCancellationToken);
		}

		// Clear Anything Previous
		this.addableTemplates = [];

		// Get Fields From Values
		const existingComponents = [
			...this.draft.entryGroups,
			...this.draft.listingGroups,
			...this.draft.sections
		];

		// Process Templates
		this.addableTemplates = await ComponentTemplateService.getAvailableTemplates(this.props.section, this.props.sectionTemplate.requirements, existingComponents);

		// Check If Loading
		if (this.state.isLoading) {

			// Indicate Is Loading
			StateService.setCancelableState(() => this.setState({ isLoading: false }), this.apiCancellationToken);
		}
	}
}
